import { createRootRoute, createRoute, createRouter, Outlet, redirect } from '@tanstack/react-router';
import { lazy, Suspense, useState } from 'react';
import { fetchDepreciationTable } from './queries/DepreciationTable';
import { componentLoader } from './react/_routes/_util/componentLoader';
import { PageNotFound } from './react/_routes/PageNotFound';
import { PrivateRoute } from './react/_routes/PrivateRoute';
import { UpdateChecker } from './react/_sw/UpdateChecker';
import { ResetPassword } from './react/Authentication/Account/ResetPassword';
import Login from './react/Authentication/LoginMfa';
import { Logout } from './react/Logout';
import { ServerErrorPage } from './react/ServerErrorPage';
import { Wrapper } from './react/Wrapper';
import type { GroupByMode } from './react/_routes/Job/JobReview/JobTopBar';
import { type TableViewSearch, validateTableViewSearch } from './react/_routes/Job/JobReview/tableViewSearch';

const AccountActivation = lazy(() => import('./react/Authentication/Account/Activation'));
const JobListing = lazy(() => componentLoader(() => import('./react/_routes/JobListing')));
const Locations = lazy(() => componentLoader(() => import('./react/_routes/Locations')));
const Settings = lazy(() => componentLoader(() => import('./react/_routes/Settings')));
const Admin = lazy(() => componentLoader(() => import('./react/_routes/Admin')));
const AdminEmployees = lazy(() => componentLoader(() => import('./react/_routes/Admin/Employees')));
const AdminCompanies = lazy(() => componentLoader(() => import('./react/_routes/Admin/Companies')));
const AdminIntegrations = lazy(() => componentLoader(() => import('./react/_routes/Admin/Integrations')));
const AdminDepreciation = lazy(() => componentLoader(() => import('./react/_routes/Admin/Depreciation')));
const AdminTax = lazy(() => componentLoader(() => import('./react/_routes/Admin/Tax')));
const AdminShopping = lazy(() => componentLoader(() => import('./react/_routes/Admin/Shopping')));
const AdminLocations = lazy(() => componentLoader(() => import('./react/_routes/Admin/Locations')));
const AdminCustody = lazy(() => componentLoader(() => import('./react/_routes/Admin/Custody')));
const Job = lazy(() => componentLoader(() => import('./react/_routes/Job')));
const JobDashboard = lazy(() => componentLoader(() => import('./react/_routes/Job/JobReview/DashboardRoute')));
const JobInventory = lazy(() => componentLoader(() => import('./react/_routes/Job/JobReview/InventoryRoute')));
const JobEvaluation = lazy(() => componentLoader(() => import('./react/_routes/Job/JobReview/EvaluationRoute')));
const JobComingSoon = lazy(() => componentLoader(() => import('./react/_routes/Job/JobReview/ComingSoonRoute')));
const JobReports = lazy(() => componentLoader(() => import('./react/_routes/Job/JobReview/Reports/ReportsRoute')));

const rootRoute = createRootRoute({
  component: () => {
    const [sideBarRef, _setSideBarRef] = useState<HTMLDivElement | null>(null);
    return (
      <>
        <UpdateChecker sideBarRef={sideBarRef} />
        <Outlet />
      </>
    );
  },
  errorComponent: ServerErrorPage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: Login,
});
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: Login,
});
const verifyMfaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-mfa',
  component: Login,
});
const verifyBackupCodeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-backup-code',
  component: Login,
});
const resendMfaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resend-mfa',
  component: Login,
});
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: Login,
});
const logoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/logout',
  component: Logout,
});

// Reset links carry only the token; the page asks for the email, which must
// never appear in a URL.
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password/$token',
  component: ResetPassword,
});
// Links emailed before that change put the email first; drop it from the
// address bar and history rather than strand a link that is still valid.
const legacyResetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password/$email/$token',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/reset-password/$token', params: { token: params.token }, replace: true });
  },
});

const activateAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/activate-account/$email/$token/$activationToken',
  component: () => (
    <Suspense>
      <AccountActivation />
    </Suspense>
  ),
});

const privateRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'private',
  component: PrivateRoute,
});

const wrapperRoute = createRoute({
  getParentRoute: () => privateRoute,
  id: 'wrapper',
  component: () => {
    const [_sideBarRef, setSideBarRef] = useState<HTMLDivElement | null>(null);
    return <Wrapper sideBarRef={setSideBarRef} />;
  },
});

const jobsRedirectRoute = createRoute({
  getParentRoute: () => wrapperRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/jobs' });
  },
});

const jobsRoute = createRoute({
  getParentRoute: () => wrapperRoute,
  path: '/jobs',
  component: () => (
    <Suspense>
      <JobListing />
    </Suspense>
  ),
});

// Layout route: loads the job and everything hanging off it, provides the
// job contexts, and renders the shared top bar. The tabs below are real
// routes so they can be linked to, bookmarked and reloaded.
const jobRoute = createRoute({
  getParentRoute: () => wrapperRoute,
  path: '/job/$key',
  component: () => (
    <Suspense>
      <Job />
    </Suspense>
  ),
});

// /job/$key on its own has no content of its own — the dashboard is the
// landing tab.
const jobIndexRoute = createRoute({
  getParentRoute: () => jobRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/job/$key/dashboard', params });
  },
});

const jobDashboardRoute = createRoute({
  getParentRoute: () => jobRoute,
  path: 'dashboard',
  component: () => (
    <Suspense>
      <JobDashboard />
    </Suspense>
  ),
});

const jobInventoryRoute = createRoute({
  getParentRoute: () => jobRoute,
  // The optional segments are the sidebar's selected room and clip
  // (/inventory/<roomKey>/<videoKey>), so a link opens on what its sender saw.
  // The first can instead be an item's key (/inventory/<itemKey>), which opens
  // the review drawer on it (useItemDrawer).
  path: 'inventory/{-$roomKey}/{-$clipKey}',
  // `q` is the inventory search box. It lives in the URL because the box is
  // rendered by the shared top bar, above this route. `group` is the sidebar's
  // grouping, and the rest the table's sort and filters (tableViewSearch.ts),
  // so a link shows what its sender saw.
  validateSearch: (search: Record<string, unknown>): { q?: string; group?: GroupByMode } & TableViewSearch => ({
    q: typeof search.q === 'string' && search.q !== '' ? search.q : undefined,
    group: search.group === 'room' || search.group === 'room-source' ? search.group : undefined,
    ...validateTableViewSearch(search),
  }),
  component: () => (
    <Suspense>
      <JobInventory />
    </Suspense>
  ),
});

// The job's items by item action. `action` is the selected action (a filter,
// so it is a search param): a company action's name, a removed action items
// still hold, or `unset` for items with none (src/react/_util/itemActions.ts).
const jobEvaluationRoute = createRoute({
  getParentRoute: () => jobRoute,
  // The optional segment is the sidebar's selected room, or an open item's key, as on the inventory.
  path: 'evaluation/{-$roomKey}',
  // The table's sort and filters are search params too, as on the inventory.
  validateSearch: (search: Record<string, unknown>): { action?: string } & TableViewSearch => ({
    action: typeof search.action === 'string' && search.action !== '' ? search.action : undefined,
    ...validateTableViewSearch(search),
  }),
  component: () => (
    <Suspense>
      <JobEvaluation />
    </Suspense>
  ),
});

// Placeholder tabs: each renders "Coming soon" until its feature is built.
const createJobComingSoonRoute = <TPath extends string>(path: TPath) =>
  createRoute({
    getParentRoute: () => jobRoute,
    path,
    component: () => (
      <Suspense>
        <JobComingSoon />
      </Suspense>
    ),
  });

const jobCustodyRoute = createJobComingSoonRoute('custody');
const jobEstimatesRoute = createJobComingSoonRoute('estimates');
// /job/$key/reports — every report generated for the job, and the way to make another.
const jobReportsRoute = createRoute({
  getParentRoute: () => jobRoute,
  path: 'reports',
  component: () => (
    <Suspense>
      <JobReports />
    </Suspense>
  ),
});

// Legacy sub-paths (/job/$key/overview, /job/$key/items, …) used to be
// swallowed by a no-op catch-all, which rendered a blank tab. Send them to the
// dashboard instead so old links still land somewhere useful.
const jobCatchAllRoute = createRoute({
  getParentRoute: () => jobRoute,
  path: '$',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/job/$key/dashboard',
      params: { key: params.key },
    });
  },
});

// Layout route for the Admin section: renders the page chrome (title, search,
// tabs) and hosts the tabs below as real routes so each can be linked to,
// bookmarked and reloaded.
const adminRoute = createRoute({
  getParentRoute: () => wrapperRoute,
  path: '/admin',
  // `q` is the header search box, which lives above the tabs.
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: typeof search.q === 'string' && search.q !== '' ? search.q : undefined,
  }),
  component: () => (
    <Suspense>
      <Admin />
    </Suspense>
  ),
});

// Employees is the default tab.
const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/admin/employees' });
  },
});

const adminEmployeesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'employees',
  component: () => (
    <Suspense>
      <AdminEmployees />
    </Suspense>
  ),
});

// One employee, open in the edit drawer over the list.
const adminEmployeeRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'employees/$employeeKey',
  component: () => (
    <Suspense>
      <AdminEmployees />
    </Suspense>
  ),
});

const adminCompaniesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'companies',
  component: () => (
    <Suspense>
      <AdminCompanies />
    </Suspense>
  ),
});

// One company, open in the edit drawer over the list.
const adminCompanyRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'companies/$companyKey',
  component: () => (
    <Suspense>
      <AdminCompanies />
    </Suspense>
  ),
});

// Per-company tabs are /admin/<tab>/$companyKey (.agent/rules/09-clean-urls.md).
// /admin/<tab> on its own renders the same component, whose company picker
// replaces the URL with the first company's. The company used to be
// `?company=`; old links are redirected here.
type AdminCompanyTabPath =
  | '/admin/integrations/$companyKey'
  | '/admin/depreciation/$companyKey'
  | '/admin/tax/$companyKey'
  | '/admin/shopping/$companyKey'
  | '/admin/locations/$companyKey'
  | '/admin/custody/$companyKey';

const redirectCompanySearch = (search: unknown, to: AdminCompanyTabPath) => {
  const { company, q } = search as { company?: unknown; q?: string };
  if (typeof company === 'string' && company !== '') {
    throw redirect({ to, params: { companyKey: company }, search: { q }, replace: true });
  }
};

const adminIntegrationsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'integrations',
  beforeLoad: ({ location }) => redirectCompanySearch(location.search, '/admin/integrations/$companyKey'),
  component: () => (
    <Suspense>
      <AdminIntegrations />
    </Suspense>
  ),
});

const adminIntegrationsCompanyRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'integrations/$companyKey',
  component: () => (
    <Suspense>
      <AdminIntegrations />
    </Suspense>
  ),
});

const adminDepreciationRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'depreciation',
  beforeLoad: async ({ location }) => {
    // An open table used to be `?table=`: send it to the table's own URL.
    const { table } = location.search as { table?: unknown };
    if (typeof table === 'string' && table !== '') await redirectToTable(table);
    redirectCompanySearch(location.search, '/admin/depreciation/$companyKey');
  },
  component: () => (
    <Suspense>
      <AdminDepreciation />
    </Suspense>
  ),
});

// One company's tables. For a while a table was /admin/depreciation/$tableKey;
// such a link lands here with a table key (`dep…`) in the company's place.
const adminDepreciationCompanyRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'depreciation/$companyKey',
  beforeLoad: async ({ params }) => {
    if (params.companyKey.startsWith('dep')) await redirectToTable(params.companyKey);
  },
  component: () => (
    <Suspense>
      <AdminDepreciation />
    </Suspense>
  ),
});

// One table, under its company.
const adminDepreciationTableRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'depreciation/$companyKey/$tableKey',
  component: () => (
    <Suspense>
      <AdminDepreciation />
    </Suspense>
  ),
});

/** For an old table link: redirect to the table under its company, or do nothing when it can't be read. */
const redirectToTable = async (tableKey: string) => {
  const table = await fetchDepreciationTable(tableKey).catch(() => null);
  if (!table) return;
  throw redirect({
    to: '/admin/depreciation/$companyKey/$tableKey',
    params: { companyKey: table.companyKey, tableKey: table.key },
    replace: true,
  });
};

const adminTaxRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'tax',
  beforeLoad: ({ location }) => redirectCompanySearch(location.search, '/admin/tax/$companyKey'),
  component: () => (
    <Suspense>
      <AdminTax />
    </Suspense>
  ),
});

const adminTaxCompanyRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'tax/$companyKey',
  component: () => (
    <Suspense>
      <AdminTax />
    </Suspense>
  ),
});

const adminShoppingRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'shopping',
  component: () => (
    <Suspense>
      <AdminShopping />
    </Suspense>
  ),
});

const adminShoppingCompanyRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'shopping/$companyKey',
  component: () => (
    <Suspense>
      <AdminShopping />
    </Suspense>
  ),
});

const adminLocationsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'locations',
  beforeLoad: ({ location }) => redirectCompanySearch(location.search, '/admin/locations/$companyKey'),
  component: () => (
    <Suspense>
      <AdminLocations />
    </Suspense>
  ),
});

const adminLocationsCompanyRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'locations/$companyKey',
  component: () => (
    <Suspense>
      <AdminLocations />
    </Suspense>
  ),
});

const adminCustodyRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'custody',
  beforeLoad: ({ location }) => redirectCompanySearch(location.search, '/admin/custody/$companyKey'),
  component: () => (
    <Suspense>
      <AdminCustody />
    </Suspense>
  ),
});

const adminCustodyCompanyRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'custody/$companyKey',
  component: () => (
    <Suspense>
      <AdminCustody />
    </Suspense>
  ),
});

// Item actions used to be their own tab; they are now a section of the
// company's edit drawer. Old links are redirected there.
const adminItemActionsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'item-actions',
  beforeLoad: ({ location }) => {
    const { company } = location.search as { company?: unknown };
    if (typeof company === 'string' && company !== '') {
      throw redirect({ to: '/admin/companies/$companyKey', params: { companyKey: company }, replace: true });
    }
    throw redirect({ to: '/admin/companies', replace: true });
  },
});

const adminItemActionsCompanyRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'item-actions/$companyKey',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/admin/companies/$companyKey', params: { companyKey: params.companyKey }, replace: true });
  },
});

const locationsRoute = createRoute({
  getParentRoute: () => wrapperRoute,
  path: '/locations',
  // `q` is the header search box.
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: typeof search.q === 'string' && search.q !== '' ? search.q : undefined,
  }),
  component: () => (
    <Suspense>
      <Locations />
    </Suspense>
  ),
});

const settingsRoute = createRoute({
  getParentRoute: () => wrapperRoute,
  path: '/settings',
  component: () => (
    <Suspense>
      <Settings />
    </Suspense>
  ),
});

const notFoundRoute = createRoute({
  getParentRoute: () => wrapperRoute,
  path: '*',
  component: PageNotFound,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  verifyMfaRoute,
  verifyBackupCodeRoute,
  resendMfaRoute,
  forgotPasswordRoute,
  logoutRoute,
  resetPasswordRoute,
  legacyResetPasswordRoute,
  activateAccountRoute,
  privateRoute.addChildren([
    wrapperRoute.addChildren([
      jobsRedirectRoute,
      jobsRoute,
      jobRoute.addChildren([
        jobIndexRoute,
        jobDashboardRoute,
        jobInventoryRoute,
        jobEvaluationRoute,
        jobCustodyRoute,
        jobEstimatesRoute,
        jobReportsRoute,
        jobCatchAllRoute,
      ]),
      adminRoute.addChildren([
        adminIndexRoute,
        adminEmployeesRoute,
        adminEmployeeRoute,
        adminCompaniesRoute,
        adminCompanyRoute,
        adminIntegrationsRoute,
        adminIntegrationsCompanyRoute,
        adminDepreciationRoute,
        adminDepreciationCompanyRoute,
        adminDepreciationTableRoute,
        adminTaxRoute,
        adminTaxCompanyRoute,
        adminShoppingRoute,
        adminShoppingCompanyRoute,
        adminLocationsRoute,
        adminLocationsCompanyRoute,
        adminCustodyRoute,
        adminCustodyCompanyRoute,
        adminItemActionsRoute,
        adminItemActionsCompanyRoute,
      ]),
      locationsRoute,
      settingsRoute,
      notFoundRoute,
    ]),
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
